import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Camera, Wine, Heart, ShoppingCart, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({ scans: 0, favorites: 0, shopping: 0 });
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [favResponse, shoppingResponse, scansResponse] = await Promise.all([
          axios.get(`${API}/favorites`, { headers: getAuthHeaders() }),
          axios.get(`${API}/shopping-list`, { headers: getAuthHeaders() }),
          axios.get(`${API}/scans/history`, { headers: getAuthHeaders() })
        ]);
        
        setStats({
          scans: scansResponse.data.length,
          favorites: favResponse.data.length,
          shopping: shoppingResponse.data.filter(i => !i.is_purchased).length
        });
        setRecentScans(scansResponse.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const statCards = [
    { icon: Camera, label: 'Total Scans', value: stats.scans, color: '#D4AF37' },
    { icon: Heart, label: 'Favorites', value: stats.favorites, color: '#EF4444' },
    { icon: ShoppingCart, label: 'Shopping Items', value: stats.shopping, color: '#10B981' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="mb-10">
        <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-2">
          Hey, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-[#A3A3A3] text-lg">
          Ready to discover new cocktails?
        </p>
      </div>

      {/* Quick Actions */}
      <div className="glass rounded-2xl p-6 mb-8 animate-fadeIn">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center">
              <Camera className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#F5F5F5]">Scan Your Bar</h2>
              <p className="text-[#A3A3A3] text-sm">
                Upload a photo to see what you can make
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/scan')}
            className="bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full px-8 py-6 gold-glow btn-press"
            data-testid="scan-now-btn"
          >
            Scan Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {statCards.map((stat, index) => (
          <div
            key={stat.label}
            className="stat-card animate-fadeIn"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}15` }}
              >
                <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-[#A3A3A3] text-sm">{stat.label}</p>
                <p className="stat-value">{loading ? '-' : stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#F5F5F5]">Recent Scans</h2>
          {recentScans.length > 0 && (
            <Button
              variant="ghost"
              className="text-[#D4AF37] hover:text-[#F4CF57]"
              onClick={() => navigate('/scan')}
            >
              View All
            </Button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 shimmer rounded-2xl" />
            ))}
          </div>
        ) : recentScans.length === 0 ? (
          <div className="empty-state glass rounded-2xl">
            <Clock className="w-12 h-12 text-[#525252] mb-4" />
            <h3 className="text-lg font-bold text-[#F5F5F5] mb-2">No scans yet</h3>
            <p className="text-[#A3A3A3] mb-4">
              Scan your bar to start discovering cocktails
            </p>
            <Button
              onClick={() => navigate('/scan')}
              className="bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full px-6 py-3 btn-press"
            >
              Start Scanning
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recentScans.map((scan, index) => (
              <div
                key={scan.id}
                className="bg-[#121212] border border-white/5 rounded-2xl p-5 card-lift animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <Camera className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#F5F5F5] font-medium">Bar Scan</p>
                    <p className="text-[#525252] text-xs">
                      {new Date(scan.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#A3A3A3] text-sm">
                    {scan.detected_bottles?.length || 0} bottles detected
                  </span>
                  <span className="category-badge">
                    {scan.matched_count || 0} cocktails
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Wine, label: 'Browse Cocktails', to: '/cocktails', color: '#D4AF37' },
          { icon: Heart, label: 'My Favorites', to: '/favorites', color: '#EF4444' },
          { icon: ShoppingCart, label: 'Shopping List', to: '/shopping-list', color: '#10B981' },
          { icon: Camera, label: 'New Scan', to: '/scan', color: '#3B82F6' }
        ].map((link, index) => (
          <button
            key={link.label}
            onClick={() => navigate(link.to)}
            className="bg-[#121212] border border-white/5 rounded-2xl p-5 text-left card-lift animate-fadeIn"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <link.icon className="w-6 h-6 mb-3" style={{ color: link.color }} />
            <p className="text-[#F5F5F5] font-medium text-sm">{link.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
